import React from "react";

/**
 * 5 polished layout renderers used by MenuRenderer:
 *   promo_card, split_layout, shape_burst, lifestyle_card, menu_grid_modern
 * Each takes (config, scale) and returns a 16:9 finished design.
 */

export function PromoCardRender({ config }) {
  const accent = config.accent_color || "#0EA5E9";
  const secondary = config.secondary_color || "#FFFFFF";
  const text = "#FFFFFF";
  const photo = config.banner_image;
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: accent, color: text }}>
      {photo && (
        <>
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${accent}f5 0%, ${accent}90 35%, transparent 70%)` }} />
        </>
      )}
      {/* Diagonal accent slash */}
      <div
        aria-hidden="true"
        className="absolute -left-[8%] top-1/2 w-[55%] h-[55%]"
        style={{ background: secondary, transform: "translateY(-50%) skewX(-18deg)", opacity: 0.95 }}
      />
      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-[5vw]">
        <div className="flex items-start justify-between">
          {config.logo_text && (
            <div className="font-bold tracking-[0.25em] uppercase" style={{ fontSize: "1.4vw", color: text, opacity: 0.9 }}>
              {config.logo_text}
            </div>
          )}
          {config.badge && (
            <div
              className="font-display font-extrabold inline-flex items-center"
              style={{ background: secondary, color: accent, padding: "0.8vw 1.6vw", fontSize: "1.8vw", borderRadius: "9999px", letterSpacing: "0.05em" }}
            >
              {config.badge}
            </div>
          )}
        </div>
        <div className="max-w-[60%]">
          <h1 className="font-display font-black leading-[0.95] tracking-tight" style={{ fontSize: "8vw", color: text }}>
            {config.headline}
          </h1>
          {config.subline && (
            <div className="font-body mt-[1.5vw]" style={{ fontSize: "2vw", color: text, opacity: 0.95 }}>
              {config.subline}
            </div>
          )}
        </div>
        {config.cta && (
          <div className="flex items-center justify-between">
            <div className="font-bold uppercase tracking-[0.25em]" style={{ fontSize: "1.4vw", color: text, opacity: 0.95 }}>
              {config.cta}
            </div>
            {config.footnote && (
              <div className="font-body opacity-80" style={{ fontSize: "1.1vw" }}>{config.footnote}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SplitLayoutRender({ config }) {
  const accent = config.accent_color || "#0EA5E9";
  const secondary = config.secondary_color || "#F1F5F9";
  const bg = config.background_color || "#FFFFFF";
  const text = config.text_color || "#0F172A";
  const photo = config.banner_image;
  return (
    <div className="w-full h-full grid grid-cols-2" style={{ background: bg, color: text }}>
      {/* Left = photo */}
      <div className="relative overflow-hidden">
        {photo
          ? <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}, ${secondary})` }} />
        }
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, transparent 60%, ${bg} 100%)` }} />
      </div>
      {/* Right = colored panel with copy */}
      <div className="relative flex flex-col justify-center p-[5vw]" style={{ background: bg }}>
        {/* Diagonal corner shape */}
        <div
          aria-hidden="true"
          className="absolute -top-[8%] -left-[8%] w-[40%] h-[40%]"
          style={{ background: accent, transform: "rotate(-12deg)", opacity: 0.18 }}
        />
        {config.logo_text && (
          <div className="font-bold tracking-[0.25em] uppercase mb-[2vw]" style={{ fontSize: "1.2vw", color: accent }}>
            {config.logo_text}
          </div>
        )}
        <h1 className="font-display font-extrabold leading-[0.95] tracking-tight" style={{ fontSize: "7vw", color: text }}>
          {config.headline}
        </h1>
        {config.subline && (
          <div className="font-body mt-[1.5vw]" style={{ fontSize: "1.9vw", color: text, opacity: 0.7 }}>
            {config.subline}
          </div>
        )}
        {config.badge && (
          <div
            className="font-display font-extrabold inline-block mt-[2.5vw]"
            style={{ background: accent, color: bg, padding: "0.7vw 1.6vw", fontSize: "2.1vw", borderRadius: "9999px", alignSelf: "flex-start" }}
          >
            {config.badge}
          </div>
        )}
        {config.cta && (
          <div className="mt-[3vw] font-bold uppercase tracking-[0.25em]" style={{ fontSize: "1.4vw", color: accent }}>
            {config.cta}
          </div>
        )}
      </div>
    </div>
  );
}

export function ShapeBurstRender({ config }) {
  const accent = config.accent_color || "#0EA5E9";
  const secondary = config.secondary_color || "#F1F5F9";
  const bg = config.background_color || "#0F172A";
  const text = config.text_color || "#FFFFFF";
  const photo = config.banner_image;
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: bg, color: text }}>
      {/* Decorative bursts */}
      <div aria-hidden="true" className="absolute -top-[20%] -right-[10%] w-[55%] h-[55%] rounded-full" style={{ background: accent, opacity: 0.18 }} />
      <div aria-hidden="true" className="absolute -bottom-[15%] -left-[10%] w-[45%] h-[45%] rounded-full" style={{ background: secondary, opacity: 0.12 }} />
      {photo && (
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 w-[45%] h-[80%] rounded-[2vw] overflow-hidden shadow-2xl">
          <img src={photo} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="relative h-full flex flex-col justify-center p-[5vw] max-w-[55%]">
        {config.logo_text && (
          <div className="font-bold tracking-[0.25em] uppercase mb-[2vw]" style={{ fontSize: "1.4vw", color: accent }}>
            {config.logo_text}
          </div>
        )}
        {config.subline && (
          <div className="font-body italic" style={{ fontSize: "2vw", color: accent }}>{config.subline}</div>
        )}
        <h1 className="font-display font-black leading-[0.95] tracking-tighter" style={{ fontSize: "9vw", color: text }}>
          {config.headline}
        </h1>
        {config.badge && (
          <div
            className="font-display font-extrabold inline-block mt-[2vw]"
            style={{ background: accent, color: bg, padding: "0.7vw 1.4vw", fontSize: "1.8vw", letterSpacing: "0.05em", alignSelf: "flex-start" }}
          >
            {config.badge}
          </div>
        )}
        {(config.items || []).length > 0 && (
          <div className="mt-[2vw] space-y-[0.8vw]">
            {(config.items || []).slice(0, 4).map((it, i) => (
              <div key={i} className="font-body" style={{ fontSize: "1.5vw", color: text, opacity: 0.9 }}>{it.label || it}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function LifestyleCardRender({ config }) {
  const accent = config.accent_color || "#0EA5E9";
  const secondary = config.secondary_color || "#FFFFFF";
  const text = config.text_color || "#FFFFFF";
  const photo = config.banner_image;
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: accent, color: text }}>
      {photo && (
        <>
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)` }} />
        </>
      )}
      {/* Corner brand block */}
      <div
        className="absolute top-0 left-0 p-[3vw] flex flex-col justify-end"
        style={{ background: accent, color: text, width: "35%", height: "40%", clipPath: "polygon(0 0, 100% 0, 78% 100%, 0 100%)" }}
      >
        {config.logo_text && (
          <div className="font-display font-extrabold tracking-tight" style={{ fontSize: "3vw" }}>{config.logo_text}</div>
        )}
      </div>
      <div className="relative h-full flex flex-col justify-end p-[5vw]">
        <h1 className="font-display font-extrabold leading-[0.95] tracking-tight" style={{ fontSize: "6vw", color: text, textShadow: "0 4px 24px rgba(0,0,0,0.4)" }}>
          {config.headline}
        </h1>
        {config.subline && (
          <div className="font-body mt-[1.2vw]" style={{ fontSize: "2vw", color: text, opacity: 0.95 }}>
            {config.subline}
          </div>
        )}
        {config.cta && (
          <div
            className="font-display font-extrabold inline-block mt-[2.5vw]"
            style={{ background: text, color: accent, padding: "0.9vw 2vw", fontSize: "1.8vw", borderRadius: "9999px", alignSelf: "flex-start" }}
          >
            {config.cta}
          </div>
        )}
      </div>
    </div>
  );
}

export function MenuGridModernRender({ config }) {
  const accent = config.accent_color || "#0EA5E9";
  const secondary = config.secondary_color || "#F8FAFC";
  const bg = config.background_color || "#FFFFFF";
  const text = config.text_color || "#0F172A";
  const photo = config.banner_image;
  const items = (config.sections || []).flatMap((s) => (s.items || []));
  return (
    <div className="w-full h-full grid grid-cols-2" style={{ background: bg, color: text }}>
      {/* Left hero photo */}
      <div className="relative overflow-hidden">
        {photo
          ? <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}, ${secondary})` }} />
        }
        <div className="absolute inset-0 flex flex-col justify-end p-[3vw]" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)" }}>
          {config.logo_text && (
            <div className="font-bold tracking-[0.25em] uppercase mb-[0.8vw]" style={{ fontSize: "1.3vw", color: "#fff", opacity: 0.9 }}>
              {config.logo_text}
            </div>
          )}
          <h1 className="font-display font-extrabold leading-[0.95] tracking-tight" style={{ fontSize: "4.5vw", color: "#fff" }}>
            {config.headline}
          </h1>
        </div>
      </div>
      {/* Right items panel */}
      <div className="relative flex flex-col justify-center p-[3vw]" style={{ background: bg }}>
        <div className="space-y-[1.5vw]">
          {items.slice(0, 6).map((it, i) => (
            <div key={it.id || i} className="flex items-baseline gap-[1.5vw] border-b pb-[1vw]" style={{ borderColor: `${accent}33` }}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold leading-tight" style={{ fontSize: "2vw", color: text }}>{it.name}</div>
                {it.description && (
                  <div className="font-body opacity-65 mt-[0.3vw]" style={{ fontSize: "1.1vw" }}>{it.description}</div>
                )}
              </div>
              <div className="font-display font-extrabold flex-shrink-0" style={{ fontSize: "2.6vw", color: accent }}>
                {String(it.price).match(/^[0-9.]/) ? `$${it.price}` : it.price}
              </div>
            </div>
          ))}
        </div>
        {config.cta && (
          <div className="mt-[2vw] text-center font-bold uppercase tracking-[0.25em]" style={{ fontSize: "1.4vw", color: accent }}>
            {config.cta}
          </div>
        )}
      </div>
    </div>
  );
}


/* ----------------------------------------------------------------------------
 * Chalkboard Grid — energetic takeaway/chippy menu board
 *
 * Captures the "EXTRAS & SIDES / CHIPS / PIES" reference look:
 *   - Black slate chalkboard background with subtle dark texture
 *   - Massive headline in white, with the second word colored (yellow accent)
 *   - Horizontal row of 6-8 items: photo on top, name below, then a yellow
 *     angled price ribbon. Vertical dotted dividers between items.
 *   - Two lower sub-section panels (e.g. CHIPS / PIES) for richer menus.
 *
 * Data model (uses existing template shape):
 *   config.sections[0]                  → main top row with up to 8 items
 *   config.sections[1] / sections[2]    → the two bottom sub-section panels
 *   each item.accent_word               → optional yellow word in name
 *   each section.accent_word            → optional yellow word in section title
 * ---------------------------------------------------------------------------*/
function ChalkText({ name, accent }) {
  // Render "CHEESE & ONION PASTY" → renders "ONION PASTY" in yellow.
  // The accent string is matched at the END of the name (case-insensitive).
  if (!name) return null;
  if (!accent) return <>{name}</>;
  const lower = name.toLowerCase();
  const target = accent.toLowerCase();
  const idx = lower.lastIndexOf(target);
  if (idx === -1) return <>{name}</>;
  return (
    <>
      {name.slice(0, idx)}
      <span style={{ color: "var(--chalk-accent)" }}>{name.slice(idx, idx + accent.length)}</span>
      {name.slice(idx + accent.length)}
    </>
  );
}

function ChalkPriceRibbon({ price, color }) {
  const formatted = String(price).match(/^[\d.]+$/) ? `£${price}` : price;
  return (
    <div
      className="font-display font-extrabold inline-block"
      style={{
        background: color,
        color: "#0A0A0A",
        padding: "0.45vw 1.1vw",
        fontSize: "2vw",
        letterSpacing: "0.02em",
        transform: "skewX(-12deg)",
        boxShadow: "0 0.4vw 1vw rgba(0,0,0,0.45)",
      }}
    >
      <span style={{ display: "inline-block", transform: "skewX(12deg)" }}>{formatted}</span>
    </div>
  );
}

export function ChalkboardGridRender({ config }) {
  const accent = config.accent_color || "#FBBF24";   // yellow ribbon + accent word
  const surface = config.secondary_color || "#0F0F0F"; // slate body
  const bg = config.background_color || "#0A0A0A";
  const text = config.text_color || "#FFFFFF";
  const photo = config.banner_image;

  const sections = config.sections || [];
  const topItems    = (sections[0]?.items || []).slice(0, 8);
  const bottomLeft  = sections[1];
  const bottomRight = sections[2];

  // Dotted vertical divider — pure CSS so it scales on any TV.
  const dottedDivider = {
    backgroundImage:
      "radial-gradient(circle, rgba(255,255,255,0.55) 25%, transparent 26%)",
    backgroundSize: "0.55vw 0.55vw",
    backgroundRepeat: "repeat-y",
    backgroundPosition: "center",
    width: "0.6vw",
    flexShrink: 0,
  };

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        background: bg,
        color: text,
        ["--chalk-accent"]: accent,
      }}
    >
      {/* Chalkboard surface texture — radial spotlight + slate noise */}
      <div className="absolute inset-0" style={{ background:
        `radial-gradient(ellipse 70% 60% at 50% 40%, ${surface} 0%, ${bg} 70%, #050505 100%)` }} />
      <div className="absolute inset-0 opacity-[0.12]" style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 6px), " +
          "repeating-linear-gradient(-45deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 9px)",
      }} />

      {/* Yellow outer frame */}
      <div className="absolute inset-[1.5vw] pointer-events-none"
           style={{ border: `0.25vw solid ${accent}AA`, borderRadius: "0.4vw" }} />

      {/* Optional hero photo top-right (e.g. chips spilling from cone) */}
      {photo && (
        <img src={photo} alt="" className="absolute"
          style={{ right: "3vw", top: "3vw", width: "22%", maxHeight: "18vh", objectFit: "contain", filter: "drop-shadow(0 1vw 1.5vw rgba(0,0,0,0.6))" }} />
      )}

      {/* HEADLINE */}
      <div className="relative" style={{ padding: "3.5vw 4vw 0" }}>
        <h1
          className="font-display font-black uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "7vw", letterSpacing: "-0.01em" }}
        >
          <ChalkText name={config.headline || "MENU"} accent={config.headline_accent || ""} />
        </h1>
        {config.subline && (
          <div className="mt-[1vw] font-body" style={{ fontSize: "1.7vw", opacity: 0.8 }}>{config.subline}</div>
        )}
      </div>

      {/* MAIN ROW */}
      {topItems.length > 0 && (
        <div className="relative flex items-start" style={{ padding: "3vw 4vw 0" }}>
          {topItems.map((it, i) => (
            <React.Fragment key={it.id || i}>
              {i > 0 && <div style={{ ...dottedDivider, height: "14vh", marginTop: "1.5vw" }} />}
              <div className="flex flex-col items-center text-center" style={{ flex: 1, padding: "0 0.6vw" }}>
                <div
                  className="font-display font-extrabold uppercase leading-[0.95] tracking-tight"
                  style={{ fontSize: "1.7vw", minHeight: "4.2vw" }}
                >
                  <ChalkText name={it.name || ""} accent={it.accent_word || ""} />
                </div>
                <div className="mt-[0.8vw]">
                  <ChalkPriceRibbon price={it.price} color={accent} />
                </div>
                {it.image && (
                  <div className="mt-[1.2vw] relative" style={{ width: "100%", aspectRatio: "1 / 1", maxHeight: "13vh" }}>
                    <img src={it.image} alt="" className="absolute inset-0 w-full h-full object-contain"
                         style={{ filter: "drop-shadow(0 0.8vw 1.2vw rgba(0,0,0,0.7))" }} />
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* BOTTOM TWO PANELS */}
      {(bottomLeft || bottomRight) && (
        <div className="relative flex" style={{ padding: "2.5vw 4vw 3.5vw", gap: "2vw" }}>
          {[bottomLeft, bottomRight].filter(Boolean).map((sec, idx) => (
            <div
              key={idx}
              className="flex-1 relative"
              style={{ background: "rgba(255,255,255,0.04)", border: `0.18vw solid ${accent}99`, borderRadius: "0.6vw", padding: "2vw" }}
            >
              <div className="flex items-start justify-between">
                <h2 className="font-display font-black uppercase leading-[0.9]"
                    style={{ fontSize: "4vw", letterSpacing: "-0.01em" }}>
                  <ChalkText name={sec.title || ""} accent={sec.accent_word || ""} />
                </h2>
                {sec.flagship_price && (
                  <ChalkPriceRibbon price={sec.flagship_price} color={accent} />
                )}
              </div>
              <div className="mt-[1.5vw] space-y-[1vw]">
                {(sec.items || []).slice(0, 6).map((it, i) => (
                  <div key={it.id || i} className="flex items-baseline justify-between gap-[1vw]">
                    <div className="font-display font-bold uppercase tracking-tight" style={{ fontSize: "1.7vw" }}>
                      <ChalkText name={it.name || ""} accent={it.accent_word || ""} />
                      {it.description && (
                        <span className="font-body normal-case opacity-70 ml-[0.6vw]" style={{ fontSize: "1.2vw", letterSpacing: 0 }}>
                          {it.description}
                        </span>
                      )}
                    </div>
                    {it.price && <ChalkPriceRibbon price={it.price} color={accent} />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LOGO / FOOTNOTE */}
      {config.logo_text && (
        <div className="absolute top-[2.5vw] right-[3vw] font-display font-extrabold uppercase tracking-[0.2em]"
             style={{ fontSize: "1.4vw", color: accent }}>
          {config.logo_text}
        </div>
      )}
      {config.footnote && (
        <div className="absolute bottom-[2vw] right-[3vw] font-body italic" style={{ fontSize: "1.1vw", opacity: 0.55 }}>
          {config.footnote}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * BurgerComboKidsRender (v2)
 * Photo-rich "Fast Food Burger Menu" — designed to match an animated
 * restaurant TV menu reference. Marble-textured dark background, FLOATING
 * burger photos (no card boxes!), 4×2 grid, small red circle price tags,
 * yellow item names, white italic descriptions, and a solid yellow
 * "BURGER MEAL" side panel with a tilted price tag.
 *
 * Config schema:
 *   headline               → big yellow title (default "BURGER MENU")
 *   eyebrow                → small cursive line above title (default "Fast Food")
 *   accent_color           → yellow item names + side panel (default warm amber)
 *   price_color            → red circle badge color (default red)
 *   background_color       → marble base color (default deep slate)
 *   items[]                → up to 8 burgers
 *     { name, description, price, image }
 *   facebook_handle, instagram_handle → optional social row
 *   kids_panel             → right "BURGER MEAL" panel
 *     { eyebrow, line1, line2, price, image, panel_color }
 * ===========================================================================*/

function BurgerSilhouette({ color = "#FFFFFF" }) {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {/* Top bun */}
      <path d="M8 26 C 12 14, 52 14, 56 26" />
      {/* Seeds */}
      <circle cx="20" cy="22" r="0.9" fill={color} stroke="none" />
      <circle cx="32" cy="19" r="0.9" fill={color} stroke="none" />
      <circle cx="44" cy="22" r="0.9" fill={color} stroke="none" />
      {/* Lettuce */}
      <path d="M8 30 Q 12 26 16 30 T 24 30 T 32 30 T 40 30 T 48 30 T 56 30" />
      {/* Cheese */}
      <path d="M8 34 L 56 34 L 53 38 L 11 38 Z" />
      {/* Patty */}
      <path d="M9 39 Q 32 44, 55 39 L 55 43 Q 32 47, 9 43 Z" />
      {/* Bottom bun */}
      <path d="M8 44 Q 32 53, 56 44" />
    </svg>
  );
}

function ChefLogo({ color = "#1A1A1A" }) {
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      {/* Chef hat */}
      <path d="M30 40 Q 22 30, 30 24 Q 35 16, 50 18 Q 65 16, 70 24 Q 78 30, 70 40" />
      <path d="M30 40 L 30 50 L 70 50 L 70 40" />
      {/* Stars */}
      <circle cx="22" cy="36" r="1.2" fill={color} stroke="none" />
      <circle cx="78" cy="38" r="1.2" fill={color} stroke="none" />
      <circle cx="80" cy="30" r="0.8" fill={color} stroke="none" />
      {/* Plate / utensils */}
      <ellipse cx="50" cy="72" rx="32" ry="12" />
      <line x1="32" y1="76" x2="32" y2="86" />
      <line x1="28" y1="68" x2="28" y2="76" />
      <line x1="36" y1="68" x2="36" y2="76" />
      <line x1="68" y1="68" x2="68" y2="86" />
      <path d="M64 68 L 64 78 L 72 78 L 72 68" />
    </svg>
  );
}

function SocialPill({ kind, handle }) {
  const path = kind === "fb"
    ? "M19 12a7 7 0 1 0-8.1 6.9v-4.9H9v-2h1.9v-1.5c0-1.9 1.1-2.9 2.8-2.9.8 0 1.7.1 1.7.1v1.9h-1c-1 0-1.3.6-1.3 1.2v1.2h2.2l-.4 2h-1.8V19A7 7 0 0 0 19 12Z"
    : "M16.3 4H7.7A3.7 3.7 0 0 0 4 7.7v8.6A3.7 3.7 0 0 0 7.7 20h8.6a3.7 3.7 0 0 0 3.7-3.7V7.7A3.7 3.7 0 0 0 16.3 4Zm-4.3 11.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Zm4.2-6.8a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8ZM12 10.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Z";
  return (
    <div className="flex items-center" style={{ gap: "0.7vw" }}>
      <div
        className="flex items-center justify-center"
        style={{
          width: "2.4vw",
          height: "2.4vw",
          background: "#FFFFFF",
          borderRadius: "50%",
        }}
      >
        <svg viewBox="0 0 24 24" width="60%" height="60%" fill="#0A0A0A">
          <path d={path} />
        </svg>
      </div>
      <span className="font-body italic" style={{ fontSize: "1.2vw", color: "#FFFFFF" }}>
        {handle}
      </span>
    </div>
  );
}

export function BurgerComboKidsRender({ config }) {
  const accent = config.accent_color || "#FFA600";           // yellow item names + side panel
  const priceColor = config.price_color || "#D11D1D";        // red circle badge
  const sideBg = config.kids_panel?.panel_color || accent;   // yellow side panel
  const bg = config.background_color || "#1B1B1F";
  const items = (config.items || []).slice(0, 8);
  const kids = config.kids_panel || {};

  // Themed marble palette — each template can customize the background tones
  // without rewriting the renderer.
  const theme = config.theme || {};
  const bgFrom = theme.bg_from || "#1F1F22";
  const bgVia  = theme.bg_via  || "#0A0A0C";
  const bgTo   = theme.bg_to   || "#18181B";
  const hi1    = theme.highlight_1 || "rgba(85,85,90,0.45)";
  const hi2    = theme.highlight_2 || "rgba(60,60,65,0.55)";
  const hi3    = theme.highlight_3 || "rgba(80,80,85,0.32)";
  const veinA  = theme.vein_a  || "rgba(255,255,255,0.05)";
  const veinB  = theme.vein_b  || "rgba(255,255,255,0.035)";

  // Side panel theming (defaults match the burger reference)
  const panelDark    = kids.text_dark    || "#0A0A0A";
  const panelOutline = kids.text_outline || "#FFFFFF";
  const panelStamp   = kids.watermark    || "#C8141A";
  const panelLogo    = kids.logo_color   || "#1A1A1A";
  const priceTagBg   = kids.price_bg     || "#FFFFFF";
  const priceTagText = kids.price_text   || "#0A0A0A";

  return (
    <div className="w-full h-full relative overflow-hidden flex" style={{ background: bg, color: "#FFFFFF" }}>
      {/* MARBLE-SLATE background — radial darks + soft veining */}
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(ellipse 60% 40% at 20% 15%, ${hi1} 0%, transparent 55%), ` +
            `radial-gradient(ellipse 55% 45% at 85% 55%, ${hi2} 0%, transparent 60%), ` +
            `radial-gradient(ellipse 70% 55% at 45% 95%, ${hi3} 0%, transparent 60%), ` +
            `linear-gradient(135deg, ${bgFrom} 0%, ${bgVia} 50%, ${bgTo} 100%)`,
        }}
      />
      {/* Subtle marble veins */}
      <div
        className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage:
            `repeating-linear-gradient(118deg, ${veinA} 0 1px, transparent 1px 28px), ` +
            `repeating-linear-gradient(62deg, ${veinB} 0 1px, transparent 1px 41px)`,
        }}
      />
      {/* Cracks / chips for realism */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 78%, rgba(255,255,255,0.05) 0 0.18vw, transparent 0.2vw), " +
            "radial-gradient(circle at 65% 22%, rgba(255,255,255,0.04) 0 0.15vw, transparent 0.16vw)",
        }}
      />

      {/* ============= LEFT — MAIN MENU 78% ============= */}
      <div
        className="relative flex flex-col"
        style={{ flex: "0 0 78%", padding: "2.4vw 2.5vw 1.5vw 3vw" }}
      >
        {/* Title row */}
        <div className="flex items-center justify-center" style={{ gap: "1.5vw" }}>
          <div style={{ width: "5vw", height: "5vw", opacity: 0.85 }}>
            <BurgerSilhouette color="#FFFFFF" />
          </div>
          <div className="text-center">
            <div
              className="font-serif italic"
              style={{ fontSize: "2vw", lineHeight: 1, color: "#FFFFFF" }}
            >
              {config.eyebrow || "Fast Food"}
            </div>
            <h1
              className="font-display font-black uppercase"
              style={{
                fontSize: "5vw",
                color: accent,
                lineHeight: 0.95,
                letterSpacing: "0.015em",
              }}
            >
              {config.headline || "BURGER MENU"}
            </h1>
          </div>
          <div style={{ width: "5vw", height: "5vw", opacity: 0.85 }}>
            <BurgerSilhouette color="#FFFFFF" />
          </div>
        </div>

        {/* 4 × 2 floating burger grid */}
        <div
          className="grid grid-cols-4 flex-1"
          style={{ columnGap: "1.6vw", rowGap: "1.2vw", marginTop: "1.8vw" }}
        >
          {items.map((item, i) => (
            <div key={item.id || i} className="flex flex-col items-center text-center">
              {/* Burger photo + red circle price */}
              <div
                className="relative"
                style={{ width: "100%" }}
                data-item-idx={i}
                data-item-id={item.id || `i${i}`}
              >
                <div
                  className="relative mx-auto"
                  style={{ width: "92%", height: "17vh" }}
                >
                  <img
                    src={item.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain"
                    data-item-image
                    style={{
                      // AI-generated transparent PNG — no mask needed.
                      filter:
                        "contrast(1.04) saturate(1.08) drop-shadow(0 1.1vw 1.4vw rgba(0,0,0,0.75))",
                      // Per-item resize handle: customers can click + drag to
                      // grow / shrink the photo from any corner.
                      transform: `scale(${item.image_scale || 1})`,
                      transformOrigin: "center center",
                      transition: "transform 0.04s linear",
                    }}
                  />
                </div>
                {/* Red circle price badge */}
                <div
                  className="absolute"
                  style={{
                    top: "-0.6vw",
                    right: "5%",
                    width: "4.2vw",
                    height: "4.2vw",
                    borderRadius: "50%",
                    background: priceColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0.4vw 0.7vw rgba(0,0,0,0.55)",
                    border: "0.18vw solid rgba(255,255,255,0.92)",
                  }}
                >
                  <span
                    className="font-display italic font-black text-white"
                    style={{ fontSize: "1.5vw", letterSpacing: "-0.02em" }}
                  >
                    ${item.price || "21"}
                  </span>
                </div>
              </div>

              {/* Item name in yellow */}
              <div
                className="font-display font-extrabold uppercase"
                style={{
                  fontSize: "1.35vw",
                  color: accent,
                  letterSpacing: "0.04em",
                  lineHeight: 1.08,
                  marginTop: "0.7vw",
                }}
              >
                {String(item.name || "").split("\n").map((ln, k) => (
                  <div key={k}>{ln}</div>
                ))}
              </div>
              {/* Italic white description */}
              {item.description && (
                <div
                  className="font-serif italic"
                  style={{
                    fontSize: "0.95vw",
                    color: "#F1F1F4",
                    opacity: 0.92,
                    lineHeight: 1.25,
                    marginTop: "0.25vw",
                    maxWidth: "92%",
                  }}
                >
                  {item.description}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Social row */}
        <div
          className="flex items-center justify-center"
          style={{ gap: "3vw", marginTop: "1vw" }}
        >
          {config.facebook_handle && (
            <SocialPill kind="fb" handle={config.facebook_handle} />
          )}
          {config.instagram_handle && (
            <SocialPill kind="ig" handle={config.instagram_handle} />
          )}
        </div>
      </div>

      {/* ============= RIGHT — YELLOW BURGER MEAL PANEL 22% ============= */}
      <div
        className="relative overflow-hidden"
        style={{
          flex: "0 0 22%",
          background: sideBg,
          padding: "2vw 1.4vw 1.4vw",
        }}
      >
        {/* Repeating diagonal watermark text */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="font-display font-black uppercase italic absolute"
              style={{
                left: "-2vw",
                top: `${56 + i * 7}%`,
                color: panelStamp,
                fontSize: "2.4vw",
                letterSpacing: "0.05em",
                whiteSpace: "nowrap",
                opacity: 0.55 + (i % 2) * 0.18,
              }}
            >
              {kids.watermark_text || "BURGER DEAL ©"}
            </div>
          ))}
        </div>

        <div className="relative flex flex-col items-center text-center h-full">
          {/* Chef hat / utensils logo */}
          <div style={{ width: "5vw", height: "5vw" }}>
            <ChefLogo color={panelLogo} />
          </div>
          {/* Eyebrow cursive */}
          <div
            className="font-serif italic"
            style={{ fontSize: "1.5vw", color: panelDark, marginTop: "0.3vw" }}
          >
            {kids.eyebrow || "Fast Food"}
          </div>
          {/* line1 (solid) */}
          <div
            className="font-display font-black uppercase"
            style={{
              fontSize: "3.6vw",
              color: panelDark,
              lineHeight: 0.9,
              letterSpacing: "0.01em",
              marginTop: "0.4vw",
            }}
          >
            {kids.line1 || "BURGER"}
          </div>
          {/* line2 (outline italic) */}
          <div
            className="font-display font-black uppercase italic"
            style={{
              fontSize: "3.6vw",
              color: "transparent",
              WebkitTextStroke: `0.18vw ${panelOutline}`,
              lineHeight: 0.9,
              letterSpacing: "0.01em",
              marginTop: "-0.3vw",
              transform: "skewX(-6deg)",
            }}
          >
            {kids.line2 || "MEAL"}
          </div>

          {/* Tilted white price tag */}
          <div
            style={{
              transform: "rotate(-12deg)",
              marginTop: "1.2vw",
            }}
          >
            <div
              className="font-display font-black italic"
              style={{
                background: priceTagBg,
                color: priceTagText,
                padding: "0.5vw 1.2vw 0.4vw",
                fontSize: "2.4vw",
                lineHeight: 1,
                boxShadow: "0 0.5vw 0.9vw rgba(0,0,0,0.35)",
                position: "relative",
              }}
            >
              {/* Small dot to mimic the "hanging tag" hole */}
              <span
                style={{
                  position: "absolute",
                  left: "0.5vw",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "0.4vw",
                  height: "0.4vw",
                  borderRadius: "50%",
                  background: sideBg,
                }}
              />
              <span style={{ paddingLeft: "0.5vw" }}>
                ${kids.price || "6.99"}
              </span>
            </div>
          </div>

          {/* Bottom meal photo (floats, no card) */}
          {kids.image && (
            <div className="mt-auto pt-[1vw] w-full">
              <img
                src={kids.image}
                alt=""
                className="block w-full object-contain"
                style={{
                  maxHeight: "26vh",
                  filter: "contrast(1.04) drop-shadow(0 1vw 1.4vw rgba(0,0,0,0.45))",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
